import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

function DropdownOptionsAdd() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(id ? true : false);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        type: 'military_status',
        value: '',
        label: '',
        displayOrder: 0,
        isActive: true,
    });

    const fetchOptionDetails = useCallback(async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/dropdown-options/${id}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                const option = data.data;
                setFormData({
                    type: option.type,
                    value: option.value,
                    label: option.label,
                    displayOrder: option.displayOrder,
                    isActive: option.isActive,
                });
            } else {
                toast.error('Failed to fetch dropdown option details');
            }
        } catch (error) {
            console.error('Error fetching dropdown option:', error);
            toast.error('Error fetching dropdown option details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (id) {
            fetchOptionDetails();
        }
    }, [id, fetchOptionDetails]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.type.trim() || !formData.value.trim() || !formData.label.trim()) {
            toast.error('Type, Value, and Label are required');
            return;
        }

        try {
            setSaving(true);

            const payload = {
                ...formData,
                type: formData.type.trim().toLowerCase(),
                value: formData.value.trim(),
                label: formData.label.trim(),
                displayOrder: parseInt(formData.displayOrder, 10) || 0,
            };

            const url = id
                ? `${Base_Url}/admin/dropdown-options/${id}`
                : `${Base_Url}/admin/dropdown-options`;

            const method = id ? 'PUT' : 'POST';

            const res = await authManager.authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (data.status === 'success') {
                toast.success(id ? 'Option updated successfully!' : 'Option created successfully!');
                setTimeout(() => navigate('/dropdownOptions'), 1500);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (err) {
            console.error('Error saving dropdown option:', err);
            toast.error('Error saving dropdown option');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">{id ? 'Edit' : 'Add'} Dropdown Option</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/dropdownOptions">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    {loading ? (
                                        <div className="card-body text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Type *</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="type"
                                                            list="dropdownOptionTypes"
                                                            value={formData.type}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., military_status, military_branch"
                                                        />
                                                        <datalist id="dropdownOptionTypes">
                                                            <option value="military_status" />
                                                            <option value="military_branch" />
                                                        </datalist>
                                                        <small className="text-muted">Groups options for one dropdown, e.g. all Military Status choices share this type</small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Value *</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="value"
                                                            value={formData.value}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., active_duty"
                                                        />
                                                        <small className="text-muted">The value stored/submitted by the frontend</small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Label *</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="label"
                                                            value={formData.label}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., Active Duty"
                                                        />
                                                        <small className="text-muted">The text shown to the user in the dropdown</small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Display Order</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="displayOrder"
                                                            value={formData.displayOrder}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                        />
                                                        <small className="text-muted">Lower numbers appear first</small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Active</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="isActive"
                                                                name="isActive"
                                                                checked={formData.isActive}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="isActive">
                                                                {formData.isActive ? 'Shown in the dropdown' : 'Hidden from the dropdown'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-footer text-center">
                                                <button
                                                    type="submit"
                                                    className="btn btn-blue"
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Saving...' : (id ? 'Update' : 'Create')}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default DropdownOptionsAdd
